import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Outlet, useLocation } from "react-router-dom";

import {
  CssBaseline,
  Paper as MuiPaper,
  Container as MuiContainer,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";

import GlobalStyle from "@/components/GlobalStyle";
import Navbar from "@/components/navbar/Navbar";
import dashboardItems, { pagesSection } from "@/components/sidebar/dashboardItems";
import Sidebar from "@/components/sidebar/Sidebar";
import Footer from "@/components/Footer";
import Settings from "@/components/Settings";
import useAuth from "@/hooks/useAuth";
import StepperSidebar from "@/components/sidebar/StepperSidebar";

const drawerWidth = 258;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const DrawerBlended = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }

  ${(props) => props.theme.breakpoints.up("xs")} {
    background: #F7F9FC;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const Paper = styled(MuiPaper)(spacing);

const Container = styled(MuiContainer)`
  height: 100%;
`;

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

interface DashboardType {
  children?: React.ReactNode;
  currentStep?: number;
}

const Dashboard: React.FC<DashboardType> = ({ children, currentStep }) => {
  const router = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Close mobile menu when navigation occurs
  useEffect(() => {
    setMobileOpen(false);
  }, [router.pathname]);

  const theme = useTheme();
  let _currentStep = currentStep; 
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const { user } = useAuth();
  if (currentStep === -1) {
    _currentStep = 0;
  }
  const selectedPages = pagesSection;
   
  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <DrawerBlended>
        {/* Mobile: temporary drawer toggled by the navbar hamburger */}
        <StepperSidebar
          PaperProps={{ style: { width: drawerWidth } }}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          steps={selectedPages}
          currentStep={_currentStep}
          sx={{ display: { xs: "block", md: "none" } }}
        />
        {/* Desktop: permanent drawer */}
        <StepperSidebar
          PaperProps={{ style: { width: drawerWidth } }}
          variant="permanent"
          steps={selectedPages}
          currentStep={_currentStep}
          sx={{ display: { xs: "none", md: "block" } }}
        />
      </DrawerBlended>
      <AppContent>
        <Navbar onDrawerToggle={handleDrawerToggle} />
        <MainContent p={isLgUp ? 12 : 5}>
          <Container maxWidth="xl">
            {children}
            <Outlet />
          </Container>
        </MainContent>
        <Footer />
      </AppContent>
      {/* <Settings /> */}
    </Root>
  );
};

export default Dashboard;
